
import { useEffect, useRef, useState } from "react";
import { NavLink, Navigate, useLocation } from "react-router-dom";
import {
  ActivityIcon as Activity,
  BellRinging,
  ChartLineUp,
  CheckCircle,
  Coins,
  Crown,
  CurrencyCircleDollar,
  GearSix,
  Gauge,
  Kanban,
  ShieldCheck,
  SignOut,
  Sparkle,
  Wallet,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";

const navItems = [
  { label: "Overview", path: "/dashboard", icon: Gauge },
  { label: "Tickets", path: "/tickets", icon: Kanban },
  { label: "Payments", path: "/payments", icon: CurrencyCircleDollar },
  { label: "Settings", path: "/settings", icon: GearSix },
];

const toneStyles = {
  gold: "border-[#D4AF37]/35 text-[#D4AF37]",
  blue: "border-sky-400/25 text-sky-300",
  red: "border-red-400/25 text-red-300",
  green: "border-emerald-400/25 text-emerald-300",
};

const shellCard =
  "rounded-none border border-white/10 bg-[#0A0A0A] shadow-none transition-all duration-200 hover:-translate-y-1 hover:border-white/20 hover:shadow-lg hover:shadow-black/20";

const ChartFrame = ({ testId, children }) => {
  const frameRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!frameRef.current || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={frameRef} className="h-[320px] min-w-0 w-full" data-testid={testId}>
      {size.width > 24 && size.height > 24 ? children(size) : null}
    </div>
  );
};

const StatusPill = ({ children, tone = "gold", testId }) => (
  <span
    className={`inline-flex items-center gap-2 border px-3 py-1 text-[11px] uppercase tracking-[0.24em] ${toneStyles[tone]}`}
    data-testid={testId}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {children}
  </span>
);

const MetricCard = ({ item, index }) => (
  <Card className={shellCard} data-testid={`overview-metric-card-${index}`}>
    <CardHeader className="space-y-3 p-6">
      <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">{item.label}</p>
      <div className="flex items-end justify-between gap-4">
        <div>
          <CardTitle className="font-['JetBrains_Mono'] text-3xl font-bold text-zinc-100">{item.value}</CardTitle>
          <p className="mt-2 text-sm text-zinc-500" data-testid={`overview-metric-delta-${index}`}>{item.delta}</p>
        </div>
        <StatusPill tone={item.tone} testId={`overview-metric-tone-${index}`}>{item.tone}</StatusPill>
      </div>
    </CardHeader>
  </Card>
);

const OverviewPage = ({ data }) => (
  <div className="space-y-8" data-testid="dashboard-overview-page">
    <section className="grid gap-6 xl:grid-cols-[1.35fr_.65fr]">
      <Card className={`${shellCard} overflow-hidden`} data-testid="hero-card">
        <CardContent className="relative p-8">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1635326537703-9b04abaf0258?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-10" />
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(212,175,55,.08),transparent_35%,transparent)]" />
          <div className="relative space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill testId="hero-live-status">Live operator mesh</StatusPill>
              {data.mocked && <StatusPill tone="blue" testId="hero-mocked-status">MOCKED DATA</StatusPill>}
            </div>
            <div className="space-y-4">
              <h1 className="max-w-3xl font-['JetBrains_Mono'] text-4xl font-black tracking-tighter text-zinc-100 sm:text-5xl">
                {data.hero.headline}
              </h1>
              <p className="max-w-2xl text-base leading-relaxed text-zinc-400" data-testid="hero-subheadline">
                {data.hero.subheadline}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={shellCard} data-testid="system-stack-card">
        <CardHeader className="border-b border-white/10 p-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">System stack</p>
          <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Live control panels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          {data.system_panels.map((panel) => (
            <div key={panel.label} className="border border-white/10 p-4" data-testid={`system-panel-${panel.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">{panel.label}</p>
                  <p className="mt-2 font-['JetBrains_Mono'] text-base text-zinc-100">{panel.status}</p>
                </div>
                <StatusPill tone={panel.status === "Healthy" || panel.status === "Live" ? "green" : panel.status === "Tight" ? "red" : "blue"}>{panel.status}</StatusPill>
              </div>
              <p className="mt-3 text-sm text-zinc-400">{panel.detail}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>

    <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
      {data.metrics.map((item, index) => <MetricCard key={item.label} item={item} index={index} />)}
    </section>

    <section className="grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
      <Card className={shellCard} data-testid="throughput-chart-card">
        <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Analytics</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Secured weekly throughput</CardTitle>
          </div>
          <ChartLineUp size={28} className="text-[#D4AF37]" />
        </CardHeader>
        <CardContent className="p-6">
          <ChartFrame testId="throughput-chart">
            {({ width, height }) => (
              <LineChart width={width} height={height} data={data.throughput}>
                <CartesianGrid stroke="#202020" vertical={false} />
                <XAxis dataKey="day" stroke="#71717A" tickLine={false} axisLine={false} />
                <YAxis stroke="#71717A" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,.1)", borderRadius: 0 }} />
                <Line type="monotone" dataKey="volume" stroke="#D4AF37" strokeWidth={3} dot={{ fill: "#D4AF37", r: 4 }} />
              </LineChart>
            )}
          </ChartFrame>
        </CardContent>
      </Card>

      <Card className={shellCard} data-testid="ticket-chart-card">
        <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Flow density</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Ticket velocity</CardTitle>
          </div>
          <Activity size={28} className="text-[#D4AF37]" />
        </CardHeader>
        <CardContent className="p-6">
          <ChartFrame testId="ticket-bar-chart">
            {({ width, height }) => (
              <BarChart width={width} height={height} data={data.throughput}>
                <CartesianGrid stroke="#202020" vertical={false} />
                <XAxis dataKey="day" stroke="#71717A" tickLine={false} axisLine={false} />
                <YAxis stroke="#71717A" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,.1)", borderRadius: 0 }} />
                <Bar dataKey="tickets" fill="#D4AF37" radius={0} />
              </BarChart>
            )}
          </ChartFrame>
        </CardContent>
      </Card>
    </section>

    <section className="grid gap-6 xl:grid-cols-[.9fr_1.1fr]">
      <Card className={shellCard} data-testid="activity-feed-card">
        <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Activity</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Operator feed</CardTitle>
          </div>
          <BellRinging size={28} className="text-[#D4AF37]" />
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[340px]" data-testid="activity-feed-scroll">
            <div className="space-y-3 p-6">
              {data.activity.map((item, index) => (
                <div key={`${item.time}-${index}`} className="border border-white/10 p-4" data-testid={`activity-item-${index}`}>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">{item.time}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-300">{item.text}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className={shellCard} data-testid="watchlist-card">
        <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Priority watchlist</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Confirmations & release queue</CardTitle>
          </div>
          <ShieldCheck size={28} className="text-[#D4AF37]" />
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          {data.payments.watchlist.map((item, index) => (
            <div key={item.ticket} className="grid gap-4 border border-white/10 p-4 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`watchlist-item-${index}`}>
              <div>
                <p className="font-['JetBrains_Mono'] text-base text-zinc-100">{item.ticket}</p>
                <p className="mt-2 text-sm text-zinc-400">{item.amount} • {item.usd}</p>
              </div>
              <StatusPill tone={item.status.includes("Confirmed") ? "green" : item.status.includes("Awaiting") ? "red" : "blue"}>{item.status}</StatusPill>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  </div>
);

const TicketsPage = ({ data }) => {
  const columns = [
    ["Awaiting roles", data.ticket_board.awaiting_roles, WarningCircle, "red"],
    ["Awaiting payment", data.ticket_board.awaiting_payment, Wallet, "blue"],
    ["Release queue", data.ticket_board.release_queue, CheckCircle, "green"],
    ["Completed", data.ticket_board.completed, Crown, "gold"],
  ];

  return (
    <div className="space-y-8" data-testid="tickets-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Live tickets board</p>
          <h2 className="mt-3 font-['JetBrains_Mono'] text-3xl font-bold tracking-tight text-zinc-100">Board the entire deal lifecycle</h2>
        </div>
        <StatusPill tone="gold" testId="tickets-board-count">{data.metrics[1].value} tickets online</StatusPill>
      </div>

      <section className="grid gap-6 xl:grid-cols-4">
        {columns.map(([title, items, Icon, tone], index) => (
          <Card key={title} className={shellCard} data-testid={`ticket-column-${index}`}>
            <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Queue {index + 1}</p>
                <CardTitle className="mt-3 font-['JetBrains_Mono'] text-lg text-zinc-100">{title}</CardTitle>
              </div>
              <Icon size={26} className="text-[#D4AF37]" />
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              {items.map((item, itemIndex) => (
                <div key={item.id} className="border border-white/10 p-4" data-testid={`ticket-card-${index}-${itemIndex}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-['JetBrains_Mono'] text-base text-zinc-100">{item.id}</p>
                      <p className="mt-2 text-sm text-zinc-400">{item.pair}</p>
                    </div>
                    <StatusPill tone={tone}>{item.value || item.confirmations || item.closed}</StatusPill>
                  </div>
                  <p className="mt-4 text-xs uppercase tracking-[0.2em] text-zinc-600">{item.address ? `${item.address} • ${item.age}` : item.age || item.closed}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  );
};

const PaymentsPage = ({ data }) => (
  <div className="space-y-8" data-testid="payments-page">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Payment operations</p>
        <h2 className="mt-3 font-['JetBrains_Mono'] text-3xl font-bold tracking-tight text-zinc-100">Confirmations, watchlists, and payout readiness</h2>
      </div>
      <StatusPill tone="blue" testId="payments-queue-status">Balanced analytics + ops</StatusPill>
    </div>

    <section className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
      <Card className={shellCard} data-testid="payments-watchlist-table-card">
        <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Confirmations</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Monitoring watchlist</CardTitle>
          </div>
          <Coins size={28} className="text-[#D4AF37]" />
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-[1fr_1.2fr_.9fr_.6fr] gap-4 border-b border-white/10 px-6 py-4 text-[11px] uppercase tracking-[0.24em] text-zinc-500">
            <span>Ticket</span>
            <span>Status</span>
            <span>Amount</span>
            <span>USD</span>
          </div>
          {data.payments.watchlist.map((item, index) => (
            <div key={item.ticket} className="grid grid-cols-[1fr_1.2fr_.9fr_.6fr] gap-4 border-b border-white/10 px-6 py-5 text-sm text-zinc-300 last:border-b-0" data-testid={`payments-watchlist-row-${index}`}>
              <span className="font-['JetBrains_Mono'] text-zinc-100">{item.ticket}</span>
              <span>{item.status}</span>
              <span>{item.amount}</span>
              <span>{item.usd}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className={shellCard} data-testid="release-queue-card">
        <CardHeader className="flex-row items-center justify-between border-b border-white/10 p-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Payout lane</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Release queue</CardTitle>
          </div>
          <Sparkle size={28} className="text-[#D4AF37]" />
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          {data.payments.release_queue.map((item, index) => (
            <div key={item.ticket} className="border border-white/10 p-4" data-testid={`release-queue-item-${index}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-['JetBrains_Mono'] text-base text-zinc-100">{item.ticket}</p>
                  <p className="mt-2 text-sm text-zinc-400">Receiver: {item.receiver}</p>
                </div>
                <StatusPill tone={item.state.includes("confirmed") ? "green" : "red"}>{item.state}</StatusPill>
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-zinc-600">{item.address}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  </div>
);

const SettingsPage = ({ data, onSaveSettings, saveLoading }) => {
  const [formState, setFormState] = useState(data.settings);

  useEffect(() => {
    setFormState(data.settings);
  }, [data.settings]);

  const updateField = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveSettings({
      ...formState,
      monitoring_confirms: Number(formState.monitoring_confirms),
      auto_close_minutes: Number(formState.auto_close_minutes),
    });
  };

  return (
    <form className="space-y-8" onSubmit={handleSubmit} data-testid="settings-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Control settings</p>
          <h2 className="mt-3 font-['JetBrains_Mono'] text-3xl font-bold tracking-tight text-zinc-100">Bot behavior and operator locks</h2>
        </div>
        <StatusPill tone="gold" testId="settings-security-pill">Admin only</StatusPill>
      </div>

      <section className="grid gap-6 xl:grid-cols-2">
        <Card className={shellCard} data-testid="settings-core-card">
          <CardHeader className="border-b border-white/10 p-6">
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Core setup</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Desk identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Bot alias</label>
              <Input
                value={formState.bot_alias}
                onChange={(event) => updateField("bot_alias", event.target.value)}
                className="h-12 rounded-none border-white/20 bg-[#0A0A0A] font-['JetBrains_Mono']"
                data-testid="settings-bot-alias-input"
              />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Monitoring confirmations</label>
                <Input
                  type="number"
                  value={formState.monitoring_confirms}
                  onChange={(event) => updateField("monitoring_confirms", event.target.value)}
                  className="h-12 rounded-none border-white/20 bg-[#0A0A0A] font-['JetBrains_Mono']"
                  data-testid="settings-monitoring-confirms-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Auto close minutes</label>
                <Input
                  type="number"
                  value={formState.auto_close_minutes}
                  onChange={(event) => updateField("auto_close_minutes", event.target.value)}
                  className="h-12 rounded-none border-white/20 bg-[#0A0A0A] font-['JetBrains_Mono']"
                  data-testid="settings-auto-close-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={shellCard} data-testid="settings-switches-card">
          <CardHeader className="border-b border-white/10 p-6">
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Execution rules</p>
            <CardTitle className="mt-3 font-['JetBrains_Mono'] text-xl text-zinc-100">Operator toggles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {[
              ["require_manual_release", "Require manual release", "Sender must approve payout manually"],
              ["dark_pool_alerts", "Dark-pool alerts", "Surface unusual ticket spikes in the feed"],
              ["payouts_live", "Payouts live", "Allow the release lane to process real withdrawals"],
            ].map(([field, label, detail], index) => (
              <div key={field} className="flex items-center justify-between gap-4 border border-white/10 p-4" data-testid={`settings-toggle-${index}`}>
                <div>
                  <p className="font-['JetBrains_Mono'] text-sm text-zinc-100">{label}</p>
                  <p className="mt-2 text-sm text-zinc-500">{detail}</p>
                </div>
                <Switch
                  checked={Boolean(formState[field])}
                  onCheckedChange={(checked) => updateField(field, checked)}
                  data-testid={`settings-switch-${field}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="submit"
          disabled={saveLoading}
          className="h-12 rounded-none border border-[#D4AF37] bg-[#D4AF37] px-6 font-['JetBrains_Mono'] text-sm uppercase tracking-[0.22em] text-black hover:bg-[#E6C15A]"
          data-testid="settings-save-button"
        >
          {saveLoading ? "Saving" : "Save settings"}
        </Button>
        <p className="text-sm text-zinc-500" data-testid="settings-note">These controls are served from the dashboard API and ready for real bot wiring later.</p>
      </div>
    </form>
  );
};

export const DashboardShell = ({ data, loading, onLogout, onSaveSettings, saveLoading }) => {
  const location = useLocation();

  if (loading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] text-zinc-100" data-testid="dashboard-loading-screen">
        <div className="border border-white/10 bg-[#0A0A0A] px-8 py-6 font-['JetBrains_Mono'] text-sm uppercase tracking-[0.22em] text-zinc-400">
          Loading prime desk...
        </div>
      </div>
    );
  }

  const currentPath = location.pathname === "/" ? "/dashboard" : location.pathname;

  let page = <OverviewPage data={data} />;
  if (currentPath === "/tickets") page = <TicketsPage data={data} />;
  if (currentPath === "/payments") page = <PaymentsPage data={data} />;
  if (currentPath === "/settings") page = <SettingsPage data={data} onSaveSettings={onSaveSettings} saveLoading={saveLoading} />;
  if (!["/dashboard", "/tickets", "/payments", "/settings"].includes(currentPath)) {
    return <Navigate replace to="/dashboard" />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100" data-testid="dashboard-shell">
      <div className="grid min-h-screen lg:grid-cols-[260px_1fr]">
        <aside className="border-r border-white/10 bg-[#050505] px-5 py-6" data-testid="dashboard-sidebar">
          <div className="flex items-center gap-4 border-b border-white/10 pb-6">
            <div className="flex h-12 w-12 items-center justify-center border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]">
              <Crown size={24} weight="duotone" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Admin terminal</p>
              <p className="mt-1 font-['JetBrains_Mono'] text-lg text-zinc-100" data-testid="sidebar-bot-alias">{data.settings.bot_alias}</p>
            </div>
          </div>

          <nav className="mt-8 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = currentPath === item.path;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between border px-4 py-4 transition-all duration-200 ${active ? "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-zinc-100" : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-100"}`}
                  data-testid={`sidebar-link-${item.label.toLowerCase()}`}
                >
                  <span className="flex items-center gap-3 font-['JetBrains_Mono'] text-sm uppercase tracking-[0.18em]">
                    <Icon size={18} weight="duotone" />
                    {item.label}
                  </span>
                  {active && <span className="h-2 w-2 bg-[#D4AF37]" />}
                </NavLink>
              );
            })}
          </nav>

          <div className="mt-8 space-y-4 border-t border-white/10 pt-6">
            <div className="border border-white/10 p-4" data-testid="sidebar-wallet-health">
              <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Wallet health</p>
              <p className="mt-3 font-['JetBrains_Mono'] text-base text-zinc-100">41.228 LTC</p>
              <p className="mt-2 text-sm text-zinc-500">Hot balance available for automated release.</p>
            </div>
            <Button
              onClick={onLogout}
              variant="outline"
              className="h-12 w-full rounded-none border-white/10 bg-transparent font-['JetBrains_Mono'] text-sm uppercase tracking-[0.18em] text-zinc-300 hover:border-white/30 hover:bg-white/5 hover:text-zinc-100"
              data-testid="dashboard-logout-button"
            >
              <SignOut size={18} weight="bold" />
              Lock dashboard
            </Button>
          </div>
        </aside>

        <div className="min-w-0">
          <header className="sticky top-0 z-10 flex flex-col gap-4 border-b border-white/10 bg-black/60 px-6 py-5 backdrop-blur-2xl sm:flex-row sm:items-center sm:justify-between" data-testid="dashboard-header">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">As of {new Date(data.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              <h3 className="mt-2 font-['JetBrains_Mono'] text-xl text-zinc-100">Operator command grid</h3>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill tone="green" testId="header-monitoring-pill">{data.settings.monitoring_confirms} confirms live</StatusPill>
              <StatusPill tone="gold" testId="header-autoclose-pill">Auto-close {data.settings.auto_close_minutes}m</StatusPill>
            </div>
          </header>

          <main className="px-6 py-8 lg:px-8">{page}</main>
        </div>
      </div>
    </div>
  );
};
